/**
 * What a thing's menu entries do, built.
 *
 * The same builder wherever acts hang: on a piece of scenery and on a
 * character. They had the same problem and now have the same answer, written
 * once, so anything learned in one place is worth knowing in the other.
 */

import { Conditions, Effects } from './Dialogue';
import { Written } from './Written';
import { Explain } from '../Explain';
import type { ItemDefinitionWire, QuestWire } from '@inverse/protocol';
import { ACT_ON_LABELS, type Act, type ActOn } from './objects';

export function ActsEditor({
  acts,
  menu,
  occasions = false,
  items,
  quests,
  characters,
  objects,
  shops,
  onChange,
}: {
  acts: readonly Act[];
  /** The menu entries this thing carries, which is what an act can hang off. */
  menu: readonly string[];
  /**
   * Whether this thing can answer things that happen to it as well as presses.
   *
   * Characters can: they are hurt, they die, they notice somebody. Scenery
   * cannot, so it is not offered a choice it has no answer to.
   */
  occasions?: boolean;
  items: readonly ItemDefinitionWire[];
  quests: readonly QuestWire[];
  characters: readonly { id: number; name: string }[];
  /**
   * The scenery there is, for the effects and conditions that name a kind.
   *
   * A name and a number, which is all any of them use. Asking for the whole
   * definition meant this builder could only be opened from somewhere holding
   * the full catalogue, which is not where a thing standing in the world is
   * edited from.
   */
  objects: readonly { id: number; name: string }[];
  shops: readonly { id: number; name: string }[];
  onChange: (acts: Act[]) => void;
}): JSX.Element {
  const writeActs = (wanted: Act[]): void => onChange(wanted);
  const setAct = (at: number, wanted: Act): void =>
    onChange(acts.map((one, index) => (index === at ? wanted : one)));

  return (
    <>
      {/* What each menu entry does, written here and run by the world. The
          same two lists a line of conversation carries, so anything learned
          writing a conversation is worth knowing here. */}
      <h4>What its menu does</h4>
      <p className="hint">
        Each entry can be given conditions and a list of what happens.
        <Explain>
          Anything without an act falls through to what the world already knew how to do, so nothing
          that worked before stops working.
        </Explain>
      </p>

      {acts.map((act, at) => (
        <div className="editor-act" key={at}>
          <div className="editor-brushes">
            {/* What sets it off. Nearly always a menu entry, but a character
                can also answer something that happens to it. */}
            {occasions ? (
              <select
                value={act.on}
                onChange={(event) => setAct(at, { ...act, on: event.target.value as ActOn })}
              >
                {(Object.keys(ACT_ON_LABELS) as ActOn[]).map((one) => (
                  <option key={one} value={one}>
                    {ACT_ON_LABELS[one]}
                  </option>
                ))}
              </select>
            ) : null}

            {act.on === 'menu' ? (
              <select
                value={act.action}
                onChange={(event) => setAct(at, { ...act, action: event.target.value })}
              >
                <option value="">Choose a menu entry</option>
                {menu.map((one) => (
                  <option key={one} value={one}>
                    {one}
                  </option>
                ))}
              </select>
            ) : null}

            {act.on === 'hurt' ? (
              <label className="editor-inline">
                <span>Down to</span>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={act.atHealth}
                  title="The share of its health, out of a hundred. Once per life."
                  onChange={(event) =>
                    setAct(at, {
                      ...act,
                      atHealth: Math.max(1, Math.min(100, Number(event.target.value) || 1)),
                    })
                  }
                />
                <span>per cent</span>
              </label>
            ) : null}

            <button
              type="button"
              className={act.hidden ? 'editor-tab on' : 'editor-tab'}
              title={
                act.hidden
                  ? 'Not offered at all while its conditions fail'
                  : 'Offered, and refused with a word when its conditions fail'
              }
              onClick={() => setAct(at, { ...act, hidden: !act.hidden })}
              disabled={act.on !== 'menu'}
            >
              {act.hidden ? 'Hidden' : 'Refused'}
            </button>

            {/* How many times it may ever run, and for whom. A lever a quest
                unlocks is pulled by everybody who finished the quest; a seal a
                world event breaks is broken once for everybody. */}
            <select
              value={act.once}
              title="How many times this may ever happen"
              onChange={(event) =>
                setAct(at, { ...act, once: event.target.value as Act['once'] })
              }
            >
              <option value="always">Every time</option>
              <option value="player">Once each</option>
              <option value="world">Once ever</option>
            </select>

            <button
              type="button"
              className="editor-tab danger"
              onClick={() => writeActs(acts.filter((_, index) => index !== at))}
            >
              Take Out
            </button>
          </div>

          {act.once !== 'always' ? (
            <Written
              key={`done-${at}`}
              placeholder={
                act.once === 'world' ? 'Somebody has been here already.' : 'You have done this.'
              }
              limit={160}
              value={act.doneSays}
              onDone={(doneSays) => setAct(at, { ...act, doneSays })}
            />
          ) : null}

          <h5>Only when</h5>
          <Conditions
            held={act.when}
            items={items}
            quests={quests}
            characters={characters}
            objects={objects}
            onChange={(when) => setAct(at, { ...act, when })}
          />
          <button
            type="button"
            className="editor-tab"
            onClick={() => setAct(at, { ...act, when: [...act.when, {}] })}
          >
            Add A Condition
          </button>

          {act.when.length > 0 ? (
            <label className="editor-field">
              <span>Refused with</span>
              <input
                type="text"
                value={act.refuses}
                placeholder="It will not budge."
                onChange={(event) => setAct(at, { ...act, refuses: event.target.value })}
              />
            </label>
          ) : null}

          <h5>Then</h5>
          <Effects
            held={act.does}
            items={items}
            quests={quests}
            shops={shops}
            characters={characters}
            objects={objects}
            onChange={(does) => setAct(at, { ...act, does })}
          />
          <button
            type="button"
            className="editor-tab"
            onClick={() => setAct(at, { ...act, does: [...act.does, {}] })}
          >
            Add Something It Does
          </button>
        </div>
      ))}

      <button
        type="button"
        className="wide"
        onClick={() =>
          writeActs([
            ...acts,
            {
              on: 'menu',
              atHealth: 50,
              action: '',
              when: [],
              does: [],
              refuses: '',
              hidden: false,
              once: 'always',
              doneSays: '',
            },
          ])
        }
      >
        Add An Act
      </button>
    </>
  );
}
